import { useParams } from 'react-router-dom'
import {
  AlertMessage,
  ConfigurableText,
  NavigationPage,
} from '@/app/components'
import { useAppSelector } from '@/hooks'
import { Vehicle } from '@/types'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import { logEvent } from '@/logging'
import Button from '@/components/Button/Button'
import { useNavigation } from '@/app/hooks'
import useRefreshPermissionsIfNeeded from '@/app/pages/schedule/vehicle/capability/useRefreshPermissionsIfNeeded'
import useNavigateToTeslaKeyPairing from '@/app/hooks/useNavigateToTeslaKeyPairing'
import PermissionSection from './PermissionSection'
import { getPermissionsEditUrl } from './utils'
import { Icon } from '@/components'
import { ButtonContainer } from '../../ava/BrandedPage'
import { VehicleLabel } from '@/app/components/TeslaVehiclePermissions/TeslaVehiclePermissions'
import { capitalizeFirstLetter } from '@/utils/stringUtilities'
import FleetUtils from '@/utils/fleet-utils/FleetUtils'
import { getScopesFromAuthorizationUrl } from '../utils/permissions'

interface PermissionHeaderProps {
  missingRequiredPermissions: boolean
}

export function PermissionHeader({
  missingRequiredPermissions,
}: PermissionHeaderProps) {
  if (missingRequiredPermissions) {
    return (
      <div className="flex flex-row items-start justify-start gap-2">
        <div className="w-5 h-5 mt-1">
          <Icon name="AlertCircleFilled" className="text-red-500" />
        </div>
        <div className="flex flex-col gap-2">
          <ConfigurableText variant="h2">
            Uh oh, we need you to update your permissions
          </ConfigurableText>
          <ConfigurableText variant="body4">
            In order to make the most use of Optiwatt, we need you to update
            some permissions below, see the settings that have "Required".
          </ConfigurableText>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-row items-start justify-start gap-2">
      <div className="flex flex-col gap-2">
        <ConfigurableText variant="h2">
          Tesla permissions successfully granted
        </ConfigurableText>
        <ConfigurableText variant="body4">
          You’ve successfully granted permissions. One last step, pair your
          Tesla virtual key so Optiwatt can securely connect and optimize
          charging.
        </ConfigurableText>
      </div>
    </div>
  )
}

export interface VerifyPermissionsViewModel {
  authorizationUrl: string | null
  selectedVehicles: Vehicle[]
  isLoadingPermissionRefresh: boolean
  permissionRefreshError: any
  firstVehicleWithMissingScopes: Vehicle | undefined
  vehicleHasMissingScopes: boolean
  handleUpdatePermissions: () => void
  handleContinue: () => void
  handleClose: () => void
}

export function useViewModel(): VerifyPermissionsViewModel {
  const navigate = useNavigation()
  const { vehicleIds } = useParams<{ vehicleIds: string }>()
  const vehicles = useAppSelector((state) => state.vehicles.vehicles) ?? []
  const { navigate: navigateToTeslaKeyPairing } = useNavigateToTeslaKeyPairing()

  const { url: authorizationUrl } = FleetUtils.useFleetAuthUrl('vehicle')

  const selectedVehicles = vehicleIds
    ? vehicleIds
        .split(',')
        .map((id) => vehicles.find((v) => v.id.toString() === id))
        .filter((v): v is Vehicle => v !== undefined)
    : []

  const {
    isLoading: isLoadingPermissionRefresh,
    error: permissionRefreshError,
  } = useRefreshPermissionsIfNeeded(selectedVehicles[0]?.id)

  const requestedScopesSet = new Set(
    getScopesFromAuthorizationUrl(authorizationUrl),
  )

  const firstVehicleWithMissingScopes = selectedVehicles.find((vehicle) => {
    const missingScopes = vehicle.car.fleet_scopes?.missing ?? []
    return missingScopes.some((scope) => requestedScopesSet.has(scope))
  })

  const vehicleHasMissingScopes = selectedVehicles.some((vehicle) => {
    const missingScopes = vehicle.car.fleet_scopes?.missing ?? []
    return missingScopes.some((scope) => requestedScopesSet.has(scope))
  })

  const handleUpdatePermissions = () => {
    // If we have a vehicle with missing scopes, redirect them to the tesla edit permissions page.
    logEvent('verify_permissions_opened_tesla_permissions')
    window.location.href = getPermissionsEditUrl()
  }

  const handleContinue = () => {
    // Move on to the tesla key connection test page.
    logEvent('verify_permissions_opened_connection_test')
    navigateToTeslaKeyPairing(selectedVehicles[0])
  }

  const handleClose = () => {
    navigate.push('/connect-device/vehicle')
  }

  return {
    authorizationUrl,
    selectedVehicles,
    isLoadingPermissionRefresh,
    permissionRefreshError,
    firstVehicleWithMissingScopes,
    vehicleHasMissingScopes,
    handleUpdatePermissions,
    handleContinue,
    handleClose,
  }
}

export default function VerifyPermissionsPage() {
  const {
    authorizationUrl,
    selectedVehicles,
    isLoadingPermissionRefresh,
    permissionRefreshError,
    firstVehicleWithMissingScopes,
    vehicleHasMissingScopes,
    handleUpdatePermissions,
    handleContinue,
    handleClose,
  } = useViewModel()

  if (!selectedVehicles.length || isLoadingPermissionRefresh) {
    return <PageLoader />
  }

  return (
    <NavigationPage
      id="verify-permissions"
      progressBar={{
        completedSteps: 1,
        totalSteps: 2,
      }}
      navigationOptions={['back', 'close']}
      onCloseClick={handleClose}
    >
      <NavigationPage.SingleCardContent>
        <div className="flex flex-col gap-8 mb-4">
          <AlertMessage
            variant="error"
            show={permissionRefreshError}
            className="mb-2"
          >
            Your permissions in Optiwatt might be out of sync with what you have
            enabled. We'll automatically sync them later.
          </AlertMessage>
          <div>
            <PermissionHeader
              missingRequiredPermissions={vehicleHasMissingScopes}
            />
            {selectedVehicles.map((vehicle) => (
              <PermissionSection
                authorizationUrl={authorizationUrl}
                key={vehicle.id}
                open={vehicle.id === firstVehicleWithMissingScopes?.id}
                missingScopes={vehicle.car.fleet_scopes?.missing ?? []}
                idealPermissions={[]}
                comingSoonPermissions={[]}
                collapsible={false}
                header={
                  <ConfigurableText variant="body4" className="mb-2">
                    <VehicleLabel
                      name={
                        vehicle.car.display_name !== ''
                          ? vehicle.car.display_name
                          : capitalizeFirstLetter(
                              vehicle.car.car_model?.make ?? '',
                            )
                      }
                      model={vehicle.car.car_model?.friendly_name ?? ''}
                      iconType={
                        vehicleHasMissingScopes
                          ? 'missingPermissions'
                          : 'connected'
                      }
                    />
                  </ConfigurableText>
                }
              />
            ))}
          </div>
        </div>
        <ButtonContainer>
          <div className="flex flex-col gap-2">
            {vehicleHasMissingScopes && (
              <Button
                variant={vehicleHasMissingScopes ? 'primary' : 'secondary'}
                id="update-permissions-button"
                onClick={handleUpdatePermissions}
              >
                Update Permissions
              </Button>
            )}
            {!vehicleHasMissingScopes && (
              <Button
                variant="primary"
                id="continue-button"
                onClick={handleContinue}
              >
                <span className="flex items-center justify-center gap-2">
                  Pair Virtual Key
                  <Icon name="ExternalLink" className="w-4 h-4" />
                </span>
              </Button>
            )}
          </div>
        </ButtonContainer>
      </NavigationPage.SingleCardContent>
    </NavigationPage>
  )
}
